import { useState, useEffect } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import type { SupabaseClient } from '@supabase/supabase-js';

export type UserRole = 'manager' | 'basic' | 'qa';

export interface UserRoleState {
  role: UserRole | null;
  /** True only for managers. Use to gate write/admin actions. */
  isManager: boolean;
  /** True only for QA auditors (read-only). */
  isQA: boolean;
  /** QA accounts are read-only — hide/disable create/edit/delete controls. */
  isReadOnly: boolean;
  /**
   * True for managers AND QA. Use to gate VISIBILITY (nav sections, manager-only
   * filters, management pages) so QA sees the same data a manager does.
   */
  hasManagerView: boolean;
  loading: boolean;
}

/**
 * One role lookup per signed-in user, shared by every caller.
 *
 * Each caller used to fetch on mount, so a project modal ran the same query
 * three times (the modal, its General panel, its Stages tab), and every panel
 * that mounted later started at `isManager: false` until its own fetch
 * returned -- which, where a control is locked for non-managers, locked a
 * manager out for that window. A request in flight is shared too, so callers
 * mounting together still make one.
 *
 * A failed lookup is not kept: the next mount tries again. A role changed
 * while the user is signed in shows on their next page load; the database
 * enforces every role rule regardless.
 */
const resolvedRoles = new Map<string, UserRole | null>();
const roleRequests = new Map<string, Promise<UserRole | null>>();

const lookupRole = (supabase: SupabaseClient, userId: string): Promise<UserRole | null> => {
  let request = roleRequests.get(userId);
  if (!request) {
    request = Promise.resolve(
      supabase
        .from('profiles')
        .select('team_members (role)')
        .eq('id', userId)
        .single()
    ).then(({ data, error }) => {
      if (error) throw error;
      const teamMember = data?.team_members as unknown as { role: UserRole } | null;
      const role = teamMember?.role ?? null;
      resolvedRoles.set(userId, role);
      return role;
    });
    roleRequests.set(userId, request);
    request.catch(() => roleRequests.delete(userId));
  }
  return request;
};

/**
 * Central hook for the current user's managerial role.
 *
 * Two distinct concerns:
 *  - VISIBILITY: use `hasManagerView` (manager OR qa) to decide what a user can see.
 *  - WRITE ABILITY: use `isReadOnly` (qa) to disable create/edit/delete controls.
 *
 * Read-only is ultimately enforced in the database (block_qa_writes trigger);
 * `isReadOnly` is purely for UX (graceful disabled controls).
 */
export const useUserRole = (): UserRoleState => {
  const session = useSession();
  const supabase = useSupabaseClient();
  const userId = session?.user?.id;
  const known = userId !== undefined && resolvedRoles.has(userId);
  const [role, setRole] = useState<UserRole | null>(known ? resolvedRoles.get(userId!) ?? null : null);
  const [loading, setLoading] = useState(!known);

  useEffect(() => {
    let cancelled = false;

    const fetchRole = async () => {
      if (!userId) {
        if (!cancelled) {
          setRole(null);
          setLoading(false);
        }
        return;
      }

      try {
        const found = await lookupRole(supabase, userId);
        if (!cancelled) setRole(found);
      } catch (error) {
        console.error('useUserRole: failed to fetch role', error);
        if (!cancelled) setRole(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRole();
    return () => {
      cancelled = true;
    };
  }, [userId, supabase]);

  const isManager = role === 'manager';
  const isQA = role === 'qa';

  return {
    role,
    isManager,
    isQA,
    isReadOnly: isQA,
    hasManagerView: isManager || isQA,
    loading,
  };
};

export default useUserRole;
